import type { QagentDocumentDict } from '../en/qagentDocument';

export const qagentDocument: QagentDocumentDict = {
  contextPanel: {
    projectResources: 'Projektresurser',
    files: {
      title: 'Filer',
      uploadTooltip: 'Ladda upp filer till projektet',
      empty: 'Inga filer än',
      uploadButton: 'Ladda upp filer',
      viewTooltip: 'Visa fil',
      deleteTooltip: 'Radera fil',
      processing: 'bearbetar...',
    },
    playbooks: {
      title: 'Playbooks',
      linkTooltip: 'Koppla befintlig playbook',
      createTooltip: 'Skapa ny playbook',
      uploadTooltip: 'Ladda upp playbook-fil',
      empty: 'Inga playbooks kopplade',
      linkButton: 'Koppla en playbook',
      viewTooltip: 'Visa playbook',
      unlinkTooltip: 'Koppla bort playbook',
    },
    activeContext: {
      files: (count: number) => `${count} fil${count !== 1 ? 'er' : ''}`,
    },
  },
  suggestions: {
    title: 'Förslag',
    pendingBadge: (count: number) => `${count} väntande`,
    types: {
      edit: 'Ändring',
      insert: 'Tillägg',
      delete: 'Borttagning',
    },
    accepted: 'Godkänt',
    rejected: 'Avvisat',
    applied: 'Tillämpat',
    accept: 'Godkänn',
    reject: 'Avvisa',
    undo: 'Ångra',
    view: 'Visa',
    acceptedCount: (count: number) => `${count} godkända`,
    rejectedCount: (count: number) => `${count} avvisade`,
    general: 'Allmänt',
    downloadWithEditsTooltip: 'Ladda ner dokumentet med de föreslagna ändringarna införda',
    download: 'Ladda ner',
    preparing: 'Förbereder …',
    paragraph: (n: number) => `Stycke ${n}`,
    more: (count: number) => `+${count} till`,
    acceptAll: 'Godkänn alla',
    rejectAll: 'Avvisa alla',
  },
  documentViewer: {
    pendingSuggestions: () => ' väntande förslag',
    allReviewed: 'Alla förslag har granskats',
    zoomOut: 'Zooma ut',
    zoomIn: 'Zooma in',
    fitToWidth: 'Anpassa till bredd',
    changes: 'Ändringar',
    hide: 'Dölj',
    show: 'Visa',
    loading: 'Läser in dokument …',
    loadFailed: 'Det gick inte att läsa in dokumentet',
    acceptedChanges: 'Godkända ändringar',
    notExported: (count: number) =>
      `${count} ${count !== 1 ? 'ändringar' : 'ändring'} har inte exporterats än`,
    noAcceptedChanges: 'Inga godkända ändringar än',
    markup: 'Markering',
    markupTooltip: 'Visa godkända ändringar som spårade ändringar (stäng av för ren vy)',
  },
  exportMenu: {
    redline: 'Word-redline',
    redlineHint: 'Spårade ändringar utan kommentarer. Redo att skicka.',
    redlineWithComments: 'Redline med kommentarer',
    redlineWithCommentsHint: 'Spårade ändringar och Queris motiveringar som Word-kommentarer. För internt bruk.',
    clean: 'Ren kopia',
    cleanHint: 'Ändringarna införda utan spårning. Sparar en ny version av filen.',
    redlineNeedsWord: 'En redline kräver en Word-fil (.docx).',
  },
  fileViewer: {
    documentFallback: 'Dokument',
    download: 'Ladda ner',
    downloadWithChanges: 'Ladda ner med ändringar',
    close: 'Stäng filvisaren',
    loading: 'Läser in dokument …',
    loadFailed: 'Det gick inte att läsa in filen',
    downloadFailed: 'Det gick inte att ladda ner filen',
    changesSkipped: (skipped: number) =>
      `${skipped} godkända ändringar kunde inte tillämpas eftersom deras ursprungliga text inte hittades i dokumentet. De utelämnades, så granska dokumentet.`,
    retry: 'Försök igen',
    unsupported: 'Filtypen stöds inte',
    downloadInstead: 'Ladda ner i stället',
  },
  playbookViewer: {
    fallbackName: 'Playbook',
    close: 'Stäng visaren',
    loading: 'Läser in playbook …',
    loadFailed: 'Det gick inte att läsa in playbooken',
    active: 'Aktiv',
    inactive: 'Inaktiv',
    ruleCount: (count: number) => `${count} ${count !== 1 ? 'regler' : 'regel'}`,
    activeCount: (count: number) => ` (${count} aktiva)`,
    noRules: 'Inga regler definierade',
  },
  chatMessage: {
    queued: 'I kö',
    yesterday: (time: string) => `Igår, ${time}`,
    dateTime: (dateStr: string, time: string) => `${dateStr}, ${time}`,
    thinking: 'Tänker',
    defaultWorking: 'Arbetar',
    cancelled: 'Avbruten',
    running: 'Kör',
    copy: 'Kopiera',
    exportWord: 'Ladda ner som Word',
    like: 'Gilla',
    dislike: 'Ogilla',
    regenerate: 'Generera om',
    queriing: 'Queri-ar',
    toolLabels: {
      thinking: 'Tänker',
      web_search: 'Söker på webben',
      search_rag_database: 'Söker i dokument',
      knowledge_search: 'Söker i dokument',
      suggest_edit: 'Föreslår redigering',
      suggest_insert: 'Föreslår infogning',
      suggest_delete: 'Föreslår radering',
      read_section: 'Läser dokumentavsnitt',
      get_diff: 'Granskar ändringar',
      plan_review: 'Planerar granskningen',
      check_redline: 'Kontrollerar ändringarna',
      withdraw_suggestion: 'Drar tillbaka ett förslag',
      remember: 'Sparar i minnet',
      generate_contract_draft: 'Tar fram avtalsutkast',
      export_document: 'Exporterar dokument',
      plan_parallel_review: 'Planerar parallell granskning',
      section_review: 'Granskar avsnitt',
      merge_results: 'Kombinerar granskningsresultat',
      generating: 'Genererar dokument',
      processingDetails: 'Bearbetar detaljer',
      processingInformation: 'Bearbetar information',
      preparingSections: 'Förbereder avsnitt',
      orderingSections: 'Sorterar avsnitt',
      generatingSections: 'Genererar avsnitt',
      checkingDuplicates: 'Kontrollerar dubbletter',
      removingDuplicates: 'Tar bort dubbletter',
      polishingContent: 'Putsar innehåll',
      finalQualityCheck: 'Slutlig kvalitetskontroll',
      fetchingPlaybookRules: 'Hämtar playbook-regler',
      generatingBlueprint: 'Planerar avtalet',
      blueprintGenerated: 'Avtalsplanen är klar',
      assemblingResults: 'Sammanställer avsnitt',
      regeneratingSection: 'Genererar om avsnitt',
      validatingContract: 'Validerar avtalet',
      savingContract: 'Sparar avtalet',
    },
    skillLabels: {
      review: 'Granskar',
    },
  },
  citations: {
    sources: (count: number) => `${count} källa${count !== 1 ? 'or' : ''}`,
    retrieved: (date: string) => `Hämtad ${date}`,
  },
  documentNotices: {
    truncated: (fileName: string, shown: number, total: number) =>
      `Bara de första ${Math.round(shown / 1000)} av ${Math.round(total / 1000)} tusen tecknen i "${fileName}" lästes. Svaret kan sakna klausuler längre in i dokumentet.`,
    summarised: (fileName: string) =>
      `"${fileName}" är långt, så assistenten såg först bara dispositionen och läser avsnitt vid behov. Kontrollera klausulhänvisningar mot dokumentet.`,
  },
};
